import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { portalApi } from '../services/portalApi';
import { queryKeys } from './queryKeys';
import { useApiMutation } from '../hooks/useApiMutation';

const EMPTY = [];
const INVALIDATE = [queryKeys.clients.all, queryKeys.properties.all, queryKeys.contacts.all];

const patchClient = (id, patch) => (c) => (c._id === id ? { ...c, ...patch } : c);

export function useTenants({ page = 1, pageSize = 10, search = '' } = {}) {
  const params = { scope: 'tenant', page, limit: pageSize, search: search || undefined };
  const query = useQuery({
    queryKey: queryKeys.clients.list(params),
    queryFn: () => portalApi.clients(params),
    placeholderData: keepPreviousData
  });
  return {
    rows: query.data?.docs || EMPTY,
    total: query.data?.totalDocs || 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error
  };
}

export function useClientQuery(id) {
  return useQuery({
    queryKey: queryKeys.clients.detail(id),
    queryFn: () => portalApi.client(id),
    enabled: Boolean(id)
  });
}

export function useCreateClient() {
  return useApiMutation({
    mutationFn: (values) => portalApi.createClient(values),
    invalidate: INVALIDATE,
    error: 'Could not add this tenant'
  });
}

export function useUpdateClient() {
  return useApiMutation({
    mutationFn: ({ _id, ...values }) => portalApi.updateClient(_id, values),
    optimistic: ({ _id, listingId, ...values }) => [
      { key: queryKeys.clients.lists(), update: (data) => ({ ...data, docs: data.docs.map(patchClient(_id, values)) }) },
      { key: queryKeys.clients.detail(_id), update: (data) => ({ ...data, client: { ...data.client, ...values } }) }
    ],
    invalidate: INVALIDATE,
    success: 'Tenant updated',
    error: 'Could not update this tenant'
  });
}

export function useDeleteClient() {
  return useApiMutation({
    mutationFn: (id) => portalApi.removeClient(id),
    optimistic: (id) => [
      {
        key: queryKeys.clients.lists(),
        update: (data) => ({
          ...data,
          docs: data.docs.filter((c) => c._id !== id),
          totalDocs: Math.max(0, (data.totalDocs || 0) - 1)
        })
      }
    ],
    invalidate: INVALIDATE,
    success: 'Tenant removed',
    error: 'Could not remove this tenant'
  });
}

export function useAddClientNote(id) {
  return useApiMutation({
    mutationFn: (text) => portalApi.addClientNote(id, text),
    optimistic: (text) => [
      {
        key: queryKeys.clients.detail(id),
        update: (data) => ({
          ...data,
          client: {
            ...data.client,
            noteThread: [...(data.client.noteThread || []), { _id: 'pending', text, by: 'You', createdAt: new Date().toISOString() }]
          }
        })
      }
    ],
    invalidate: [queryKeys.clients.detail(id)],
    error: 'Could not save this note'
  });
}
